import { CarrierMapping, CarrierMappingMatchType } from "../../team";
import { HttpsUrl } from "../../url";
import { CarrierDefinition } from "./carrier-definition";
import { applyCarrierMappingRules } from "./carrier-mapping";
import {
  CanonicalCarrierCode,
  carrierRegistry,
  getCanonicalCarrierCode,
} from "./carrier-registry";
import { inferCarrierFromTrackingCodeThirdPartyLibrary } from "./tracking-number-fallback";

const ECN_SUFFIX = "_ecn";

// cSpell:disable

/**
 * Resolve a carrier code to its definition from the registry.
 *
 * Resolution flow:
 * 1. Normalize carrier string (lowercase, trim, spaces/hyphens to underscores)
 * 2. Evaluate global carrier rules (corrections, aliases, tracking code inference)
 * 3. Handle _ecn suffix convention
 * 4. Look up in registry
 *
 * When trackingCode is provided:
 * - Correction rules can override a known carrier based on tracking code evidence
 * - Inference rules can identify the carrier when no carrier is provided
 */
export function resolveCarrier(
  carrier: string | undefined,
  trackingCode?: string,
): CarrierResolution {
  const normalized = carrier ? normalizeCarrierString(carrier) : undefined;

  const mapped = applyCarrierMappingRules({
    carrier: normalized,
    trackingCode,
    rules: GLOBAL_CARRIER_RULES,
  });

  if (mapped) {
    const result = lookupInRegistry(
      mapped,
      CarrierResolutionSource.GLOBAL_RULES,
    );
    if (result.found) {
      return result;
    }
  }

  // Second pass: if carrier was provided but unresolvable, try with carrier=undefined
  // so UNSPECIFIED tracking code inference rules can fire as fallback.
  if (normalized && trackingCode) {
    const inferred = applyCarrierMappingRules({
      carrier: undefined,
      trackingCode,
      rules: GLOBAL_CARRIER_RULES,
    });
    if (inferred) {
      const result = lookupInRegistry(
        inferred,
        CarrierResolutionSource.TRACKING_CODE_INFERENCE,
      );
      if (result.found) {
        return result;
      }
    }
  }

  if (trackingCode) {
    const librarySlug =
      inferCarrierFromTrackingCodeThirdPartyLibrary(trackingCode);
    if (librarySlug) {
      const result = lookupInRegistry(
        librarySlug,
        CarrierResolutionSource.TRACKING_NUMBER_LIBRARY,
      );
      if (result.found) {
        return result;
      }
    }
  }

  return { found: false, originalCarrier: carrier };
}

/**
 * Get the human-readable display name for a carrier code.
 * Returns the registry display name if found, otherwise returns the original carrier string.
 */
export function getCarrierDisplayName(carrier: string | undefined): string {
  if (!carrier) {
    return "";
  }
  const resolution = resolveCarrier(carrier);
  return resolution.found ? resolution.definition.displayName : carrier;
}

/**
 * Get the carrier's native tracking page URL for a given tracking code.
 * Returns undefined if the carrier is unknown or has no URL template.
 */
export function getCarrierTrackingUrl(
  carrier: string | undefined,
  trackingCode: string | undefined,
): HttpsUrl | undefined {
  if (!carrier || !trackingCode) {
    return undefined;
  }

  const resolution = resolveCarrier(carrier);
  if (!resolution.found || !resolution.definition.trackingUrl) {
    return undefined;
  }

  return resolution.definition.trackingUrl(trackingCode);
}

// in general, keep the most specific (rarely used) rules at the top
// and the most general, broadly-applying rules at the bottom.
const GLOBAL_CARRIER_RULES: readonly CarrierMapping[] = [
  // ── Section 1: Carrier corrections ──
  // When the provided carrier + tracking code together indicate the carrier is wrong.
  {
    value: "tele_post",
    matchType: CarrierMappingMatchType.EXACT,
    targetCarrier: "cirro_ecommerce",
    trackingCode: {
      value: "^CR\\d{12}$",
      matchType: CarrierMappingMatchType.REGEX,
    },
  },

  // ── Section 2: Name aliases ──
  // Map common carrier name variations to canonical registry slugs.
  alias("australiapost", "australia_post"),
  alias("benben", "ben_ben"),
  alias("apg", "apg_ecommerce"),
  alias("apc", "apc_postal_logistics"),
  alias("dhlecs", "dhl_ecommerce"),
  alias("dhl_ecs", "dhl_ecommerce"),
  alias("dhlglobalmail", "dhl_ecommerce"),
  alias("dhlglobalmailinternational", "dhl_ecommerce"),
  alias("dhlgm", "dhl_ecommerce"),
  alias("osm", "usps"),
  alias("osmworldwide", "usps"),
  alias("passportinternational", "passport"),
  alias("tusk_logistics", "tusk"),
  alias("upsmi", "ups_mail_innovations"),
  alias("cirrotrack", "cirro_ecommerce"),
  alias("ontrack", "on_trac"),
  alias("usps+vip", "usps"),
  alias("shopify", "usps"),
  alias("collective", "usps"),
  alias("ontrac", "on_trac"),
  alias("passportship", "passport"),
  alias("gofo", "gofo_express"),
  alias("4px", "four_px"),
  alias("amazon_logistics", "amazon"),
  alias("amazon_logistics_uk", "amazon_uk"),
  alias("amazon_logistics_us", "amazon"),
  alias("amazon_shipping_+_amazon_mcf", "amazon"),
  alias("amazon_shipping_amazon_mcf", "amazon"),
  alias("amzn", "amazon"),
  alias("apc_lax", "apc_postal_logistics"),
  alias("apc_nj", "apc_postal_logistics"),
  alias("apc_ord", "apc_postal_logistics"),
  alias("arcbest_panther", "arc_best"),
  alias("asendia", "asendia_usa"),
  alias("asendiausa", "asendia_usa"),
  alias("bettertrucks", "better_trucks"),
  alias("canada_post_one_balance", "canada_post"),
  alias("canadapost", "canada_post"),
  alias("cdl_last_mile", "cdl"),
  alias("laser_ship", "lasership"),
  alias("chinapost", "china_post"),
  alias("cirro", "cirro_ecommerce"),
  alias("cirroparcel", "cirro_parcel"),
  alias("city_mail", "city_mail_se"),
  alias("citymail_pk", "city_mail_pk"),
  alias("citymail_se", "city_mail_se"),
  alias("columbuslastmile", "cdl"),
  alias("courierexpress", "courier_express"),
  alias("couriers_please", "couriersplease"),
  alias("daipost", "dai_post"),
  alias("deutschepostuk", "deutsche_post_uk"),
  alias("dhl", "dhl_ecommerce"),
  alias("dhl_ecommerce_us", "dhl_ecommerce"),
  alias("dhl_global_mail", "dhl_ecommerce"),
  alias("dhlexpress", "dhl_express"),
  alias("dhlpaket", "dhl_paket"),
  alias("dhlparcel", "dhl_parcel"),
  alias("dpdnl", "dpd_nl"),
  alias("dpduk", "dpd_uk"),
  alias("estesexpress", "estes"),
  alias("federal_express", "fedex"),
  alias("fedex_2_day", "fedex"),
  alias("fedex_ground", "fedex"),
  alias("fedexcrossborder", "fedex_cross_border"),
  alias("fedexsmartpost", "fedex_smart_post"),
  alias("fleetoptics", "fleet_optics"),
  alias("gls_us", "gls"),
  alias("gso", "gls"),
  alias("hermes", "evri"),
  alias("ics_courier", "ics"),
  alias("interlinkexpress", "interlink_express"),
  alias("japan_post_(en)", "japan_post"),
  alias("japan_post_(ja)", "japan_post"),
  alias("js_exp", "js_express"),
  alias("landmarkglobal", "landmark_global"),
  alias("loomisexpress", "loomis_express"),
  alias("new_zealand_post", "nz_post"),
  alias("ninjavan_int", "ninjavan"),
  alias("omniparcel", "omni_parcel"),
  alias("osm_worldwide", "usps"),
  alias("osm_worldwideparcel_select", "usps"),
  alias("osmworldwidev2", "osm_worldwide"),
  alias("passportglobal", "passport"),
  alias("pitneybowes", "pitney_bowes"),
  alias("poczta_polska", "poland_post"),
  alias("r+l_carriers", "r_plus_l_carriers"),
  alias("rl_carriers", "r_plus_l_carriers"),
  alias("royalmail", "royal_mail"),
  alias("royalmailv3", "royal_mail_v3"),
  alias("rr_donnelley", "epost_global"),
  alias("rrdonnelley", "epost_global"),
  alias("saia_freight", "saia"),
  alias("second_closet", "gobolt"),
  alias("sfexpress", "sf_express"),
  alias("shipbob", "ship_bob"),
  alias("telepost", "tele_post"),
  alias("tforce_final_mile", "tforce"),
  alias("tforceconcise", "tforce"),
  alias("toll_ipec", "toll"),
  alias("tusklogistics", "tusk"),
  alias("united_delivery_service", "uds"),
  alias("upsmailinnovations", "ups_mail_innovations"),
  alias("us_postal_service", "usps"),
  alias("usps_first_class_mail", "usps"),
  alias("usps_parcel_select", "usps"),
  alias("wanbexpress", "wanb_express"),
  alias("shipwizmo", "wizmo"),
  alias("xpologistics", "xpo"),
  alias("yunexpress", "yun_express"),

  // ── Section 3: Tracking code inference ──
  // Ordered from most specific to least specific.
  inference("^BEN\\d{8}BEN$", "ben_ben"),
  inference("^SY[A-Z]{2}\\d{9}$", "sunyou"),
  inference("^VEHO[A-Z]{3}\\d{9}$", "veho"),
  inference("^VH[A-Z0-9]{12}$", "veho"),
  inference("^TBA[0-9]{12}$", "amazon"),
  inference("^AQ\\d{9}CN$", "china_post"),
  inference("^[CD][0-9]{14}$", "on_trac"),
  // ACI Logistix: 26 digits containing "00000034"
  inference("^(?=.*00000034)\\d{26}$", "aci_logistix"),
  inference("^GFUS", "gofo_express"),
  inference("^EPG", "epost_global"),
  inference("^CR\\d{12}$", "cirro_ecommerce"),
  inference("^UU", "uniuni"),
  inference("^1Z[A-Z0-9]{16}$", "ups"),
  inference("^[A-Z]{2}\\d{9}CA$", "canada_post"),
  // Intelligent Mail Barcode (IMB): 20 digits starting with "0031"
  inference("^0031\\d{16}$", "pitney_bowes"),
  // USPS Intelligent Mail barcode: "420" + 5-digit zip prefix
  inference("^420\\d{5}", "usps"),
  // USPS standard tracking numbers
  inference("^(9[2-5][0-9]{18,24}|[A-Z]{2}[0-9]{9}US)$", "usps"),
  // Last resort: bare 12-digit codes are often FedEx but ambiguous
  inference("^\\d{12}$", "fedex"),
];
// cSpell:enable

// Build slug index at module load time for O(1) lookups.
// Each carrier gets two keys: the canonical slug and the underscore-stripped form for fuzzier matching.
const aliasIndex = Object.fromEntries(
  Object.values(carrierRegistry).flatMap((d) => [
    [d.slug, d],
    [d.slug.replace(/_/g, ""), d],
  ]),
) as Record<string, CarrierDefinition>;

export enum CarrierResolutionSource {
  GLOBAL_RULES = "global_rules",
  TRACKING_CODE_INFERENCE = "tracking_code_inference",
  TRACKING_NUMBER_LIBRARY = "tracking_number_library",
}

export type CarrierResolution =
  | {
      found: true;
      definition: CarrierDefinition;
      matchedAlias: string;
      /** True if _ecn suffix was detected, indicating non-EasyPost provider should be preferred */
      preferNonEasyPost: boolean;
      canonicalCarrier: CanonicalCarrierCode;
      source: CarrierResolutionSource;
    }
  | { found: false; originalCarrier: string | undefined };

function normalizeCarrierString(carrier: string): string {
  return carrier
    .toLowerCase()
    .trim()
    .replace(/[\s-]+/g, "_")
    .replace(/[^a-z0-9_()+]/g, "");
}

function lookupInRegistry(
  carrier: string,
  source: CarrierResolutionSource,
): CarrierResolution {
  const hasEcnSuffix = carrier.endsWith(ECN_SUFFIX);
  const stripped = hasEcnSuffix
    ? carrier.slice(0, -ECN_SUFFIX.length)
    : carrier;

  const definition = aliasIndex[stripped];
  if (definition) {
    return {
      found: true,
      definition,
      matchedAlias: stripped,
      preferNonEasyPost: hasEcnSuffix,
      canonicalCarrier: getCanonicalCarrierCode(definition),
      source,
    };
  }

  return { found: false, originalCarrier: carrier };
}

/**
 * A common pattern of mapping where the raw carrier name is unambiguously
 * referring to a known carrier.
 */
function alias(value: string, targetCarrier: string): CarrierMapping {
  return { value, matchType: CarrierMappingMatchType.EXACT, targetCarrier };
}

/**
 * A common pattern of mapping where the carrier is unknown
 * but can be inferred from the tracking code.
 */
function inference(
  trackingCodePattern: string,
  targetCarrier: string,
): CarrierMapping {
  return {
    value: "",
    matchType: CarrierMappingMatchType.UNSPECIFIED,
    targetCarrier,
    trackingCode: {
      value: trackingCodePattern,
      matchType: CarrierMappingMatchType.REGEX,
    },
  };
}
